import React, { useState } from 'react';
import {
  Avatar, Box, Button, Chip, Divider, IconButton, Link as MuiLink, Menu, MenuItem, Stack, TextField, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  AddReactionOutlined as AddReactionIcon, Close as RemoveIcon, MoreVert as MoreIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { buildApiUrl } from '../../utils/unifiedApi';
import {
  formatChatTime, initialsFor, isSystemMessage, senderDisplayName, splitLinks,
} from '../../utils/chatFormat';
import ChatGif from './ChatGif';
import ChatPhoto from './ChatPhoto';
import { MAX_MESSAGE_LENGTH } from './ChatComposer';
import EmojiPickerPopover from './EmojiPickerPopover';

export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

const AVATAR_SIZE = 32;

const MessageText = ({ content, isOwn }) => (
  <>
    {splitLinks(content).map((segment, index) => (segment.type === 'link' ? (
      <MuiLink
        key={index}
        href={segment.value}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: isOwn ? 'inherit' : 'primary.main', textDecorationColor: 'currentColor', wordBreak: 'break-all' }}
      >
        {segment.value}
      </MuiLink>
    ) : (
      <React.Fragment key={index}>{segment.value}</React.Fragment>
    )))}
  </>
);

const Reactions = ({ reactions, currentUserTenantId, onToggle, isOwn }) => (
  <Stack
    direction="row"
    spacing={0.5}
    useFlexGap
    flexWrap="wrap"
    justifyContent={isOwn ? 'flex-end' : 'flex-start'}
    sx={{ mt: 0.5 }}
  >
    {reactions.map((reaction) => {
      const mine = reaction.userTenantIds?.includes(currentUserTenantId);
      return (
        <Tooltip key={reaction.emoji} title={(reaction.names || []).join(', ')}>
          <Chip
            size="small"
            variant={mine ? 'filled' : 'outlined'}
            color={mine ? 'primary' : 'default'}
            label={`${reaction.emoji} ${reaction.count}`}
            onClick={() => onToggle(reaction.emoji)}
            aria-label={`${reaction.emoji} ${reaction.count}${mine ? ', you reacted' : ''}`}
            aria-pressed={mine}
            sx={{ height: 24, '& .MuiChip-label': { px: 0.75 } }}
          />
        </Tooltip>
      );
    })}
  </Stack>
);

/** Inline editor for the sender's own message. Enter saves on desktop (new line on mobile, like the composer). */
const MessageEditor = ({
  message, isMobile, onSave, onCancel,
}) => {
  const [draft, setDraft] = useState(message.content || '');
  const [saving, setSaving] = useState(false);
  const trimmed = draft.trim();
  // Photo and GIF captions may be cleared; text messages need some text.
  const hasMedia = Boolean(message.attachment || message.gif);
  const canSave = !saving && (trimmed.length > 0 || hasMedia);

  const save = async () => {
    if (!canSave) return;
    if (trimmed === (message.content || '').trim()) {
      onCancel();
      return;
    }
    setSaving(true);
    const saved = await onSave(trimmed);
    if (!saved) setSaving(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    } else if (!isMobile && event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
      event.preventDefault();
      save();
    }
  };

  return (
    <Box data-testid={`chat-message-editor-${message.id}`}>
      <TextField
        multiline
        autoFocus
        fullWidth
        size="small"
        minRows={1}
        maxRows={8}
        value={draft}
        disabled={saving}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={(event) => {
          const { length } = event.target.value;
          event.target.setSelectionRange?.(length, length);
        }}
        placeholder={hasMedia ? 'Add a caption' : undefined}
        inputProps={{ maxLength: MAX_MESSAGE_LENGTH, 'aria-label': 'Edit message' }}
        sx={{ bgcolor: 'background.paper' }}
      />
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: 0.5 }}>
        {!isMobile && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            Enter to save · Esc to cancel
          </Typography>
        )}
        <Button size="small" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="small" variant="contained" onClick={save} disabled={!canSave}>Save</Button>
      </Stack>
    </Box>
  );
};

/** Automatic announcements such as "Sam Striker joined the team": centred and muted, no avatar or reactions. */
const SystemMessage = ({ message, canDelete, onRequestDelete }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="center"
    spacing={0.5}
    sx={{ my: 1, px: 1 }}
    data-testid={`chat-message-${message.id}`}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      component="p"
      role="status"
      sx={{
        bgcolor: 'grey.100',
        borderRadius: 3,
        px: 1.5,
        py: 0.5,
        textAlign: 'center',
        wordBreak: 'break-word',
        fontStyle: message.isDeleted ? 'italic' : 'normal',
      }}
    >
      {message.isDeleted ? 'This message was deleted' : message.content}
      {' · '}
      {formatChatTime(message.createdAt)}
    </Typography>
    {canDelete && !message.isDeleted && (
      <Tooltip title="Remove announcement">
        <IconButton size="small" aria-label="Remove announcement" onClick={() => onRequestDelete(message)} sx={{ opacity: 0.6 }}>
          <RemoveIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    )}
  </Stack>
);

const ChatMessageItem = ({
  message, isOwn, showHeader, canDelete, onRequestDelete, onToggleReaction, currentUserTenantId, onMessageSender,
  canEdit = false, onEditMessage,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [editing, setEditing] = useState(false);
  const name = senderDisplayName(message);
  const reactions = message.reactions || [];

  const react = (emoji) => {
    setMenuAnchor(null);
    setPickerAnchor(null);
    onToggleReaction(message, emoji);
  };

  if (isSystemMessage(message)) {
    return <SystemMessage message={message} canDelete={canDelete} onRequestDelete={onRequestDelete} />;
  }

  const canMessageSender = !isOwn && Boolean(onMessageSender);
  const canEditMessage = canEdit && Boolean(onEditMessage) && !message.isDeleted;
  const isEdited = !message.isDeleted && (message.isEdited || Boolean(message.editedAt));
  const isEditing = editing && canEditMessage;
  const hasMedia = !message.isDeleted && Boolean(message.attachment || message.gif);

  const saveEdit = async (content) => {
    const saved = await onEditMessage(message, content);
    if (saved) setEditing(false);
    return saved;
  };

  return (
    <Stack
      direction={isOwn ? 'row-reverse' : 'row'}
      spacing={1}
      alignItems="flex-end"
      sx={{ mt: showHeader ? 1.5 : 0.25 }}
      data-testid={`chat-message-${message.id}`}
    >
      {!isOwn && (
        showHeader ? (
          <Avatar
            src={message.senderPhotoUrl ? buildApiUrl(message.senderPhotoUrl) : undefined}
            alt={name}
            sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, fontSize: '0.8rem' }}
          >
            {initialsFor(name)}
          </Avatar>
        ) : (
          <Box sx={{ width: AVATAR_SIZE, flexShrink: 0 }} />
        )
      )}

      <Box sx={{ maxWidth: { xs: '80%', sm: '70%' }, minWidth: 0, width: isEditing ? { xs: '80%', sm: '70%' } : undefined }}>
        {showHeader && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="baseline"
            justifyContent={isOwn ? 'flex-end' : 'flex-start'}
            sx={{ px: 0.5, mb: 0.25 }}
          >
            {!isOwn && (
              <MuiLink
                component={RouterLink}
                to={`/player/${message.senderUserTenantId}`}
                variant="caption"
                underline="hover"
                sx={{ fontWeight: 600, color: 'text.primary', wordBreak: 'break-word' }}
              >
                {name}
              </MuiLink>
            )}
            <Typography variant="caption" color="text.secondary">
              {formatChatTime(message.createdAt)}
            </Typography>
          </Stack>
        )}

        {isEditing ? (
          <MessageEditor message={message} isMobile={isMobile} onSave={saveEdit} onCancel={() => setEditing(false)} />
        ) : (
          <Box
            sx={{
              px: hasMedia ? 0.5 : 1.5,
              py: hasMedia ? 0.5 : 1,
              borderRadius: 2,
              bgcolor: isOwn ? 'primary.main' : 'grey.100',
              color: isOwn ? 'primary.contrastText' : 'text.primary',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              opacity: message.isDeleted ? 0.7 : 1,
            }}
          >
            {message.isDeleted ? (
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>This message was deleted</Typography>
            ) : (
              <>
                {message.attachment && <ChatPhoto attachment={message.attachment} />}
                {message.gif && <ChatGif gif={message.gif} />}
                {message.content && (
                  <Typography
                    variant="body2"
                    component="div"
                    sx={hasMedia ? { px: 1, pt: 0.75, pb: 0.25 } : undefined}
                  >
                    <MessageText content={message.content} isOwn={isOwn} />
                  </Typography>
                )}
                {isEdited && (
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ fontStyle: 'italic', opacity: 0.75, ...(hasMedia ? { px: 1, pb: 0.25 } : {}) }}
                  >
                    (edited)
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}
        {!message.isDeleted && reactions.length > 0 && (
          <Reactions
            reactions={reactions}
            currentUserTenantId={currentUserTenantId}
            onToggle={(emoji) => onToggleReaction(message, emoji)}
            isOwn={isOwn}
          />
        )}
      </Box>

      {!message.isDeleted && !isEditing && (
        <>
          <IconButton
            size="small"
            aria-label="Message actions"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{ opacity: 0.6, alignSelf: 'center' }}
          >
            <MoreIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <Box sx={{ display: 'flex', px: 1, pb: 0.5 }} role="group" aria-label="Quick reactions">
              {QUICK_REACTIONS.map((emoji) => (
                <IconButton key={emoji} size="small" onClick={() => react(emoji)} aria-label={`React with ${emoji}`}>
                  <Box component="span" sx={{ fontSize: '1.3rem', lineHeight: 1 }}>{emoji}</Box>
                </IconButton>
              ))}
              <IconButton
                size="small"
                onClick={(event) => setPickerAnchor(event.currentTarget)}
                aria-label="More reactions"
              >
                <AddReactionIcon fontSize="small" />
              </IconButton>
            </Box>
            {(canDelete || canMessageSender || canEditMessage) && <Divider />}
            {canMessageSender && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onMessageSender(message);
                }}
              >
                {`Message ${message.senderFirstName || name}`}
              </MenuItem>
            )}
            {canEditMessage && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setEditing(true);
                }}
              >
                Edit message
              </MenuItem>
            )}
            {canDelete && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onRequestDelete(message);
                }}
              >
                Delete message
              </MenuItem>
            )}
          </Menu>
          <EmojiPickerPopover
            open={Boolean(pickerAnchor)}
            anchorEl={pickerAnchor}
            onClose={() => setPickerAnchor(null)}
            onSelect={react}
            isMobile={isMobile}
          />
        </>
      )}
    </Stack>
  );
};

export default ChatMessageItem;
